/**
 * useStuck — detect when a `position: sticky` element becomes pinned.
 *
 * WHY A SENTINEL INSTEAD OF A SCROLL LISTENER
 * A `scroll` handler fires on every frame and forces you to read layout each
 * time. Instead we drop a zero-height sentinel immediately BEFORE the sticky
 * element and let an IntersectionObserver watch it. The observer only wakes at
 * the boundary — the instant the sentinel scrolls out of view the sticky
 * element has reached the top, so `stuck` flips to true (and back to false on
 * the way up). One callback per crossing, nothing on the frames in between.
 *
 * Usage:
 *   const { sentinelRef, stuck } = useStuck();
 *   <div ref={sentinelRef} aria-hidden className="h-px" />
 *   <div className={cn('sticky top-0', stuck && 'shadow-md')}>…</div>
 */

import { useEffect, useRef, useState } from 'react';

export function useStuck<T extends HTMLElement = HTMLDivElement>() {
  const sentinelRef = useRef<T>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), {
      threshold: 0,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return { sentinelRef, stuck };
}

export default useStuck;
